import React from 'react';
import { useParams, Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import SEO from './SEO';

const formatDate = (iso) => {
  // Parse as local date to avoid timezone shifting
  const [y, m, d] = (iso || '').split('-').map(Number);
  const date = new Date(y, (m || 1) - 1, d || 1);
  return date.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

export default function ChangelogEntry() {
  const { slug } = useParams();
  const [meta, setMeta] = React.useState(null);
  const [content, setContent] = React.useState('');
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);

  React.useEffect(() => {
    let mounted = true;
    async function load() {
      try {
        const [indexRes, mdRes] = await Promise.all([
          fetch('/changelog/index.json', { cache: 'no-store' }),
          fetch(`/changelog/${slug}.md`, { cache: 'no-store' }),
        ]);
        if (!indexRes.ok) throw new Error('Failed to load changelog index');
        if (!mdRes.ok) throw new Error('Entry not found');
        const list = await indexRes.json();
        const found = list.find((i) => i.slug === slug) || null;
        const text = await mdRes.text();
        if (!mounted) return;
        setMeta(found);
        setContent(text);
        setLoading(false);
      } catch (e) {
        if (!mounted) return;
        setError(e.message);
        setLoading(false);
      }
    }
    load();
    return () => (mounted = false);
  }, [slug]);

  const title = meta?.title || 'Changelog';
  const date = meta?.date ? formatDate(meta.date) : '';

  return (
    <div className="pt-28 pb-16 px-6">
      <SEO
        title={`${title} – Boxia`}
        description={meta?.summary || 'Product update from Boxia'}
        url={typeof window !== 'undefined' ? window.location.href : ''}
      />
      <div className="max-w-3xl mx-auto">
        <div className="mb-6 flex items-center gap-3 text-sm text-gray-600 dark:text-gray-400">
          <Link to="/changelog" className="hover:text-purple-600 dark:hover:text-purple-300">← Back to changelog</Link>
          {meta?.tags?.length ? (
            <div className="ml-auto flex items-center gap-2">
              {meta.tags.map((t) => (
                <span key={t} className="inline-flex items-center rounded-lg bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300 px-2.5 py-1 text-xs font-medium">{t}</span>
              ))}
            </div>
          ) : null}
        </div>

        <header className="mb-6">
          <div className="text-xs text-gray-500 dark:text-gray-400">{date}</div>
          <h1 className="mt-1 text-3xl font-bold text-gray-900 dark:text-gray-100">{title}</h1>
          {meta?.summary && (
            <p className="mt-2 text-gray-600 dark:text-gray-400">{meta.summary}</p>
          )}
        </header>

        {loading && (
          <div className="text-gray-500 dark:text-gray-400">Loading…</div>
        )}
        {error && (
          <div className="text-red-600 dark:text-red-400">{error}</div>
        )}

        {!loading && !error && (
          <article className="text-gray-800 dark:text-gray-200">
            <ReactMarkdown
              components={{
                h1: (props) => (
                  <h2 {...props} className="text-2xl font-bold mt-8 mb-3" />
                ),
                h2: (props) => (
                  <h3 {...props} className="text-xl font-semibold mt-8 mb-3" />
                ),
                h3: (props) => (
                  <h4 {...props} className="text-lg font-semibold mt-6 mb-2" />
                ),
                p: (props) => (
                  <p {...props} className="leading-7 mt-4 text-gray-700 dark:text-gray-300" />
                ),
                ul: (props) => (
                  <ul {...props} className="list-disc pl-6 space-y-1 mt-3" />
                ),
                ol: (props) => (
                  <ol {...props} className="list-decimal pl-6 space-y-1 mt-3" />
                ),
                li: (props) => <li {...props} className="marker:text-gray-400" />,
                hr: (props) => <hr {...props} className="my-8 border-gray-200 dark:border-gray-800" />,
                a: (props) => (
                  <a
                    {...props}
                    className="text-purple-600 dark:text-purple-300 hover:underline"
                    target={props.href?.startsWith('http') ? '_blank' : undefined}
                    rel={props.href?.startsWith('http') ? 'noopener noreferrer' : undefined}
                  />
                ),
                code: (props) => (
                  <code {...props} className="rounded bg-gray-100 dark:bg-gray-800 px-1.5 py-0.5 text-sm" />
                ),
                pre: (props) => (
                  <pre {...props} className="rounded-lg bg-gray-900 text-gray-100 p-4 overflow-x-auto" />
                ),
              }}
            >
              {content}
            </ReactMarkdown>
          </article>
        )}
      </div>
    </div>
  );
}
